import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { reportCustomError } from '@utils/owlReporter';
import { PipeConfig, SourcePipeConfig } from '../../interface';
import PushCode from '../pushSource/key';
import CustomSourceKey from '../customSource/key';
import {
    NewOrderDetailMessage,
    PhfOrderBaseInfo,
    isPhfOrderVo,
    IndexOrderInfo,
    isIndexOrderVo,
} from '../customSource/newOrderDetailPoller';
import {
    EnhancedNewOrderPushData,
    PushOrderUserRole,
    OrderBusinessType,
    NewOrderPushData as RawNewOrderPushData,
    PhfOrderDetail,
} from './baseBranch/newOrderPushPipe';

export type NewOrderPushData =
    | {
          origin: RawNewOrderPushData<PushOrderUserRole.NORMAL>;
          detail: PhfOrderDetail;
      }
    | {
          origin: RawNewOrderPushData<PushOrderUserRole.PHF_MAIN>;
          detail: PhfOrderDetail | null;
      }
    | {
          origin: RawNewOrderPushData<PushOrderUserRole.PHF_SUB>;
          detail: null;
      };

/** 拼好饭订单分流 */
export const phfOrderBranch: SourcePipeConfig<PushCode, CustomSourceKey> = {
    push: {
        [PushCode.NEW_ORDER_1]: (source$: Observable<EnhancedNewOrderPushData>) => {
            return source$.pipe(
                filter(data => {
                    return (
                        // prettier-ignore
                        // 普通拼好饭订单的 user_role 为 -1
                        (
                            data.origin.user_role === PushOrderUserRole.NORMAL
                            // 如果 detail 为 null（自动接单请求详情失败或非自动接单），走默认的线上逻辑（由普通订单分流处理），
                            // 拼好饭分流仅需要处理 businessType 为 2 的。
                            && (data.detail !== null && data.detail.businessType === OrderBusinessType.PHF)
                        )
                        // 拼单一起送主单的 user_role 为 1，由拼好饭分流处理
                        || data.origin.user_role === PushOrderUserRole.PHF_MAIN
                        // 拼单一起送子单的 user_role 为 2，由拼好饭分流处理
                        || data.origin.user_role === PushOrderUserRole.PHF_SUB
                    );
                }),
            ) as Observable<NewOrderPushData>;
        },
    } as PipeConfig<PushCode>,
    custom: {
        // TODO: 由 newOrders 逐渐迁移到 newIndexOrders，灰度过程中存在两个结构的 VO（PhfOrderBaseInfo 或 ParsedIndexOrderInfo），全量后可以移除 PhfOrderBaseInfo。
        newOrderDetailPoller: source$ => {
            return source$.pipe(
                // 拼好饭分流仅获取拼好饭订单
                map<NewOrderDetailMessage, (PhfOrderBaseInfo | IndexOrderInfo)[]>(data => {
                    return data.newIndexOrders?.length
                        ? data.newIndexOrders.filter(
                              order => order.businessType === OrderBusinessType.PHF,
                          )
                        : data.newOrders.filter(isPhfOrderVo);
                }),
                map(orders => {
                    return orders.filter(order => {
                        if (!isIndexOrderVo(order)) {
                            if (order.businessType === OrderBusinessType.PHF) return true;

                            // 理论上该分支不会走到，只要是旧的订单 VO 应当都属于拼好饭
                            reportCustomError({
                                name: 'Abnormal data from poller[newOrderDetail]',
                                message: '轮询订单数据异常',
                                category: 'Poller Exception',
                                level: 'WARN',
                                tags: {
                                    businessType: order.businessType,
                                    wmOrderViewId: order.wm_order_id_view_str,
                                    wmPoiId: order.wm_poi_id,
                                },
                            });
                            return false;
                        }
                        return true;
                    });
                }),
                // 如果消息里没有拼好饭订单，无需产生消息
                filter(orders => Boolean(orders.length)),
            );
        },
    },
} as SourcePipeConfig<PushCode, CustomSourceKey>;

export default phfOrderBranch;
